import React, { useContext, useEffect, useState, useRef } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import HighchartsBoost from 'highcharts/modules/boost';
import HighchartsAccessibility from 'highcharts/modules/accessibility';
import { formatLineData, formatVolume,  formatTooltipVolume,filterNasdaqWorkingHours, formatPlotBands, formatScatterData } from './support';
import { formatDateTime, formatDateUTC,  } from '../../../../../Controllers/utils';
import { ChartContainer } from '../../../../StyledComponents/AppStyledComponents';
import './support/index.css'
import { StateContext } from '../../../../../Controllers/context/state';

HighchartsBoost(Highcharts);
HighchartsAccessibility(Highcharts);

const StockChart = ({ 
    carouselPlotLine,
    height, 
    hoverPoint, 
    setChartMinMax, 
    setClickedPoint, 
    setHoverPoint, 
    symbol, 
    width,
    historicalData,
    selectedPercentile,
    selectedInfoScore,
    selectedDate,
    selectedEventType
}) => {

    const chartRef = useRef(null);
    const [isScatter, setIsScatter] = useState(false);
    const { chartType, setShowChartInfoWindow } = useContext(StateContext);
    const [options, setOptions] = useState({
        chart: {
            backgroundColor: '#0a1926',
            height: height - 300,
        },
        credits: {
            text: '',
            href: '',
        },
        yAxis: [
            {
                gridLineColor: 'transparent',
                labels: {
                    style: {
                        color: 'white',
                    },
                    align: 'center',
                    y: 5,
                },
                top:'10%',
                height: '80%',
                resize: {
                    enabled: true,
                },
                crosshair: {
                    snap: false,
                },
            },
            {
                gridLineColor: 'transparent',
                labels: {
                    style: {
                        color: 'transparent',
                    },
                },
                title: {
                    text: '',
                    style: {
                        color: 'transparent',
                    },
                },
                top: '70%',
                height: '30%',
            },
        ],
        series: [
            {
                name: '',
                data: [],
                color: chartType === '10m' ? '#4f4f40' : '#E1D9D1',
                type: 'line',
                yAxis: 0,
                enableMouseTracking: true,
                dashStyle: chartType === '10m' ? 'ShortDash' : 'Solid',

                id:'line'
            },
            {
                name: '',
                type: 'column',
                id: 'volume',
                color: '#5DD39E',
                data: [],
                yAxis: 1,
            },
            {
                type: 'scatter',
                id: `scatter`,
                data: [],
                yAxis: 0,
                marker: {
                    symbol: 'circle',
                },
                zIndex:5,
                point: {
                    events: {
                        mouseOver: function () {
                            const newTs = this.x;
                            if (!this.selected) { 
                                setHoverPoint({ time_utc: newTs });
                            }
                        },
                        mouseOut: function () {
                            if (!this.selected) {
                                setHoverPoint(null);
                            }
                        },
                        click: function (event) {
                            handleClick(this.x, event);
                        },
                    },
                },
            },
            {
                name: '',
                data: [],
                color: chartType === '10m' ? '#E1D9D1' : '#4f4f40',
                type: 'line',
                yAxis: 0,
                enableMouseTracking: true,
                id: 'extendedHours',
            },
        ],
        rangeSelector: {
            enabled: false,
        },
        navigator: {
            enabled: true,
            adaptToUpdatedData: true,
            series: {
                name: '',
                color: '#4f4f4f'
            },
            xAxis: {
                ordinal: false, 
            },
        },
        scrollbar: {
            enabled: false,
        },
        tooltip: {
            shared: true,
            split: false,    
            positioner: function (labelWidth, labelHeight, point) {
                let chart = this.chart;
                let tooltipX = point.plotX - labelWidth / 2; 
                let tooltipY;
            
                if (point.plotY < labelHeight + 20) {
                    tooltipY = chart.plotTop + chart.plotHeight - 25;
                } else {
                    tooltipY = chart.plotTop + 10;
                }
            
                if (tooltipX + labelWidth > chart.plotWidth) {
                    tooltipX = chart.plotWidth - labelWidth;
                } else if (tooltipX < 0) {
                    tooltipX = 0;
                }
            
                return {
                    x: tooltipX + chart.plotLeft, 
                    y: tooltipY
                };
            },
            
            borderWidth: 0,
            backgroundColor: isScatter ? 'transparent' :  '#0c2233',
            shadow: false,
            style: {
                color: 'white',
                fontSize: '10px',
            },
            formatter: function () {
                if (!this?.points?.length) {
                    setIsScatter(true);
                    return false;
                }

                const pricePoint = this.points.find(point => point?.series?.options?.id === 'line');
                if (!pricePoint?.point?.custom) {
                    return false;
                }

                setIsScatter(false);
                const point = pricePoint.point.custom;
                
                // Format the values
                const price = point.close.toFixed(1);
                const pointDate = chartType === 'D' ? formatDateUTC(point.time_utc) : formatDateTime(point.time_utc);
                const formattedVolume = formatTooltipVolume(point.volume);

                return `<span>$${price} ${formattedVolume} ${pointDate}</span>`;
            },
            enabled: true,
        },
        plotOptions: {
            series: {
                stickyTracking: false,
                turboThreshold: 5000,
                states: {
                    inactive: {
                        opacity: 1
                    }
                },
                dataGrouping: {
                    enabled: false
                }
            },
            line: {
                stickyTracking: true,
            },
            scatter: {
                marker: {
                    states: {

                        select: {
                            fillColor: 'grey',
                            lineColor: 'white',
                            lineWidth: 2,
                            radius: 7,
                        },
                    },
                },

            },
            column: {
                opacity: 0.7,
                states: {
                    hover: {
                        opacity: 0.7,
                    },
                    inactive: {
                        opacity: 0.7
                    }
                },
            },
        },
        xAxis: {
            crosshair: {
                snap: false,
            },
            events: {
                afterSetExtremes: async function (e) {
                    setChartMinMax({ min: e.min, max: e.max });
                },
                setExtremes: function (e) {
                    setHoverPoint(null);
                    setClickedPoint(null);
                    setShowChartInfoWindow(false);
                },
            },
            labels: {
                style: {
                    color: 'white',
                },
            },
        },
        boost: {
            enabled: true,
        },
        time:{
            useUTC:false,
        }
    });

    useEffect(() => {
        if (chartRef.current) {
            const chart = chartRef.current.chart;
            const dataIndex = chart.series[2].data.findIndex((point) => point.x === hoverPoint?.time_utc);
            const selectPoint = chart.series[2].data[dataIndex];
            const scatterSeries = chart.series[2];

            if (selectPoint) {
                scatterSeries.data.forEach(function (point) {
                    if (point === selectPoint) {
                        point.select(true, false);
                    } else {
                        point.select(false, true);
                    }
                });
            }

            setOptions((prevOptions) => {
                const newXAxis = { ...prevOptions.xAxis };
                if (hoverPoint) {
                    newXAxis.plotLines = [
                        {
                            value: hoverPoint.time_utc,
                            color: 'grey',
                            width: 2,
                            id: 'dynamicPlotLine',
                            dashStyle: 'dash',
                        },
                    ];
                } 
                else {
                    newXAxis.plotLines = (newXAxis.plotLines || []).filter(
                        (line) => line.id !== 'dynamicPlotLine'
                    );
                }

                return {
                    ...prevOptions,
                    xAxis: newXAxis
                };
            });
        }
    }, [hoverPoint]);
    

    const handleClick = (point) => {
        const scatterSeries = chartRef.current.chart.series[2];
        const clickedPoint = scatterSeries.data.find(p => p.x === point);
        
        if (clickedPoint) {
            setClickedPoint(null);  
            setTimeout(() => {     
                setClickedPoint(point);
            }, 0);
        }
    };

    const calculateDateRanges = (lastTimestamp, chartType, selectedDate) => {
        let date;
        
        if (selectedDate && chartType === 'D') {
            date = new Date(selectedDate * 1000);
        } else {
            date = new Date(lastTimestamp);
        }

        let maxDateUTC = Date.UTC(
            date.getFullYear(), 
            date.getMonth(), 
            date.getDate(),
            chartType === '10m' ? date.getHours() : 0,
            chartType === '10m' ? date.getMinutes() : 0,
            chartType === '10m' ? date.getSeconds() : 0
        );
        
        if (chartType === 'D') {
            maxDateUTC += 24 * 60 * 60 * 1000; 
        }

        const pastDate = new Date(date);
        const daysToSubtract = chartType === 'D' ? 180 : 3;
        pastDate.setDate(pastDate.getDate() - daysToSubtract);
        
        let pastDateUTC = Date.UTC(
            pastDate.getFullYear(), 
            pastDate.getMonth(), 
            pastDate.getDate(),
            chartType === '10m' ? pastDate.getHours() : 0,
            chartType === '10m' ? pastDate.getMinutes() : 0,
            chartType === '10m' ? pastDate.getSeconds() : 0
        );
        
        return {
            maxDateUTC,
            pastDateUTC
        };
    };

    useEffect(() => {
        if (!historicalData?.length || !chartRef.current) return;

        const formattedLineData = formatLineData(historicalData);
        const formattedVolumeData = formatVolume(historicalData);
        const workingHoursData = filterNasdaqWorkingHours(historicalData);
        const plotBands = formatPlotBands(historicalData);
        const scatterData = formatScatterData(historicalData, chartType, symbol, selectedPercentile, selectedInfoScore, selectedEventType);
        
        const lastScatterPoint = scatterData[scatterData.length - 1];
        
        setOptions(prevOptions => ({
            ...prevOptions,
            series: [
                {
                    ...prevOptions.series[0],
                    data: formattedLineData
                },
                {
                    ...prevOptions.series[1],
                    data: formattedVolumeData
                },
                {
                    ...prevOptions.series[2],
                    data: scatterData,
                    marker: {
                        radius: selectedEventType === 'All' ? 4 : 5,
                    },
                },
                {
                    ...prevOptions.series[3],
                    data: workingHoursData,
                    name: '',
                    color: chartType === '10m' ? '#E1D9D1' : '#4f4f40',
                    type: 'line',
                    yAxis: 0,
                    id: 'extendedHours',
                }
            ],
            xAxis: {
                ...prevOptions.xAxis,
                plotLines: [
                    ...(hoverPoint ? [{
                        value: hoverPoint.time_utc,
                        color: 'grey',
                        width: 2,
                        id: 'dynamicPlotLine',
                        dashStyle: 'dash',
                    }] : []),
                    ...(lastScatterPoint ? [{
                        value: lastScatterPoint.x,
                        color: 'grey',
                        width: 2,
                        id: 'lastScatterPlotLine',
                        dashStyle: 'dash',
                    }] : [])
                ]
            }
        }));

   
        
    }, [historicalData, selectedPercentile, selectedInfoScore, selectedEventType]);

    useEffect(() => {
        if (!historicalData?.length || !chartRef.current) return;

        const { maxDateUTC, pastDateUTC } = calculateDateRanges(
            historicalData[historicalData.length - 1].time_utc,
            chartType,
            selectedDate
        );

        const chart = chartRef.current.chart;
        if (chartType === 'D') {
            chart.xAxis[0].setExtremes(pastDateUTC, maxDateUTC);
        }
        
        const plotBands = formatPlotBands(historicalData);
        chart.xAxis[0].update({ plotBands: plotBands }, false);

    }, [historicalData, selectedDate, chartType]);

    useEffect(() => {
        chartRef.current.chart.update({
            chart: {
                width: width,
            }
        })
    }, [width])


    return (
        <ChartContainer
            style={{ width: `${width}px`}}
        >
            <HighchartsReact
                containerProps={{ style: { width: `${width}px`}}}
                highcharts={Highcharts}
                constructorType={'stockChart'}
                options={options}
                ref={chartRef}
            />
        </ChartContainer>
    );
};

export default StockChart;
